/**
 * Artefact Templates API service
 *
 * Provides functions for managing artefact templates at organisation and case levels.
 */

import { apiClient } from './api'
import type {
  ArtefactTemplate,
  CollectorSettings,
  CreateArtefactTemplateInput,
  ResolvedDefaultTemplate,
  TemplateActiveUsage,
  TestBuildInput,
  TestBuildResult,
  UpdateArtefactTemplateInput,
  UpsertCollectorSettingsInput,
} from '../types/artefact-templates'

// =============================================================================
// ORGANISATION-LEVEL API
// =============================================================================

/**
 * List artefact templates for the current organisation
 */
export async function listOrgTemplates(): Promise<ArtefactTemplate[]> {
  return apiClient.get<ArtefactTemplate[]>('/org/artefact-templates')
}

/**
 * Create an organisation-level artefact template
 */
export async function createOrgTemplate(
  input: CreateArtefactTemplateInput
): Promise<ArtefactTemplate> {
  return apiClient.post<ArtefactTemplate>('/org/artefact-templates', input)
}

/**
 * Update an organisation-level artefact template
 */
export async function updateOrgTemplate(
  templateId: string,
  input: UpdateArtefactTemplateInput
): Promise<ArtefactTemplate> {
  return apiClient.put<ArtefactTemplate>(`/org/artefact-templates/${templateId}`, input)
}

/**
 * Delete an organisation-level artefact template
 */
export async function deleteOrgTemplate(templateId: string): Promise<void> {
  await apiClient.delete(`/org/artefact-templates/${templateId}`)
}

/**
 * Get a single organisation-level artefact template (with collector settings)
 */
export async function getOrgTemplate(templateId: string): Promise<ArtefactTemplate> {
  return apiClient.get<ArtefactTemplate>(`/org/artefact-templates/${templateId}`)
}

// =============================================================================
// COLLECTOR SETTINGS API
// =============================================================================

/**
 * Get collector settings for a template
 */
export async function getCollectorSettings(templateId: string): Promise<CollectorSettings | null> {
  return apiClient.get<CollectorSettings | null>(
    `/org/artefact-templates/${templateId}/collector-settings`
  )
}

/**
 * Upsert collector settings for a template
 */
export async function upsertCollectorSettings(
  templateId: string,
  input: UpsertCollectorSettingsInput
): Promise<CollectorSettings> {
  return apiClient.put<CollectorSettings>(
    `/org/artefact-templates/${templateId}/collector-settings`,
    input
  )
}

/**
 * Delete collector settings for a template
 */
export async function deleteCollectorSettings(templateId: string): Promise<void> {
  await apiClient.delete(`/org/artefact-templates/${templateId}/collector-settings`)
}

// =============================================================================
// ACTIVE USAGE & TEST BUILD API
// =============================================================================

/**
 * Get active usage counts for a template (deployment warning)
 */
export async function getTemplateActiveUsage(templateId: string): Promise<TemplateActiveUsage> {
  return apiClient.get<TemplateActiveUsage>(`/org/artefact-templates/${templateId}/active-usage`)
}

/**
 * Record a test build for a template (updates last_tested metadata)
 */
export async function recordTestBuild(
  templateId: string,
  input: TestBuildInput
): Promise<TestBuildResult> {
  return apiClient.post<TestBuildResult>(`/org/artefact-templates/${templateId}/test-build`, input)
}

// =============================================================================
// CASE-LEVEL API
// =============================================================================

/**
 * List artefact templates for a case (includes full hierarchy)
 */
export async function listCaseTemplates(caseId: string): Promise<ArtefactTemplate[]> {
  return apiClient.get<ArtefactTemplate[]>(`/cases/${caseId}/artefact-templates`)
}

/**
 * Create a case-level artefact template
 */
export async function createCaseTemplate(
  caseId: string,
  input: CreateArtefactTemplateInput
): Promise<ArtefactTemplate> {
  return apiClient.post<ArtefactTemplate>(`/cases/${caseId}/artefact-templates`, input)
}

/**
 * Update a case-level artefact template
 */
export async function updateCaseTemplate(
  caseId: string,
  templateId: string,
  input: UpdateArtefactTemplateInput
): Promise<ArtefactTemplate> {
  return apiClient.put<ArtefactTemplate>(`/cases/${caseId}/artefact-templates/${templateId}`, input)
}

/**
 * Delete a case-level artefact template
 */
export async function deleteCaseTemplate(caseId: string, templateId: string): Promise<void> {
  await apiClient.delete(`/cases/${caseId}/artefact-templates/${templateId}`)
}

/**
 * Get the resolved default artefact template for a case
 */
export async function getResolvedDefault(caseId: string): Promise<ResolvedDefaultTemplate> {
  return apiClient.get<ResolvedDefaultTemplate>(
    `/cases/${caseId}/artefact-templates/resolved-default`
  )
}
